import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useReceiveTestMessage } from 'services/repositories/relationships/relationships';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { isCurrentPartnerTheMessageReceiver } from 'support/helpers/relationships/relationships';
import { MessageV2DTO, ReceiverEnvelopeState, TransactionFlowState } from 'support/types';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';

import { ActionConfig } from '../components/types';

type UseDeliverTestMessageActionResult = {
  action: ActionConfig;
  isDelivering: boolean;
};

export const useDeliverTestMessageAction = (message: MessageV2DTO): UseDeliverTestMessageActionResult => {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: deliverTestMessage, isLoading: isDelivering } = useReceiveTestMessage();
  const { isFetching: isRefetchingMessage } = useMessageWithProcessSpecification({
    variables: { messageId: message.id },
  });

  const handleDeliver = () => {
    setIsModalOpen(false);
    if (!message.relationship_id) {
      return;
    }
    deliverTestMessage(
      { id: message.relationship_id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: useMessageWithProcessSpecification.getKey({ messageId: message.id }),
          });
          addNotification(
            { title: t('transactions:show.actionButtons.deliverTestMessageModal.successNotification') },
            NotificationType.success,
          );
        },
        onError: (error) => {
          logError(error);
          addNotification(
            { title: t('transactions:show.actionButtons.deliverTestMessageModal.errorNotification') },
            NotificationType.error,
          );
        },
      },
    );
  };

  return {
    action: {
      label: 'transactions:show.actionButtons.deliverTestMessage',
      group: 'processing',
      onClick: () => setIsModalOpen(true),
      analyticsId: 'transactions:deliver_test_message',
      loading: isDelivering || isRefetchingMessage,
      disabled: () => isDelivering || isRefetchingMessage,
      // Mirror the "Awaiting Delivery" badge gate in deriveState: the receiver sees this action only
      // while a test message's receiver envelope is in the simulated-delivery state.
      test: (msg) =>
        isCurrentPartnerTheMessageReceiver(msg, currentPartner?.id) &&
        msg.receiver_envelope?.state === ReceiverEnvelopeState.deliverySimulated &&
        msg.flow === TransactionFlowState.test &&
        Boolean(msg.relationship_id),
      renderModal: () => (
        <DeliverTestMessageModal open={isModalOpen} setOpen={setIsModalOpen} onConfirm={handleDeliver} />
      ),
    },
    isDelivering,
  };
};

type DeliverTestMessageModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
};

const DeliverTestMessageModal = ({ open, setOpen, onConfirm }: DeliverTestMessageModalProps) => {
  const { t } = useTranslation();

  return (
    <BasePopup open={open} setOpen={setOpen}>
      <BasePopup.Body>
        <h1 className="text-lg font-semibold text-gray-900">
          {t('transactions:show.actionButtons.deliverTestMessageModal.title')}
        </h1>
        <div className="mt-2 flex flex-col gap-6">
          <p className="text-sm text-gray-500">
            {t('transactions:show.actionButtons.deliverTestMessageModal.description')}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button analyticsId="deliver_test_message_cancel" variant="secondary" onClick={() => setOpen(false)}>
              <Translate i18nKey="common:cancel" />
            </Button>
            <Button analyticsId="deliver_test_message_confirm" variant="primary" onClick={onConfirm}>
              <Translate i18nKey="transactions:show.actionButtons.deliverTestMessageModal.confirm" />
            </Button>
          </div>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
